export const STAGE_TYPES = {
  MESSAGE: "message",
  TEXT: "text",
  TEMPLATE: "template",
  BUTTON: "button",
  MENU: "menu",
  DROPDOWN: "dropdown",
  RADIO_POPUP: "radio_popup",
  MEDIA: "media",
  USER_INPUT: "user_input",
  API: "api",
  CONDITION: "condition",
  SET_VARIABLE: "set_variable",
  DELAY: "delay",
  FLOW_LINK: "flow_link",
  END: "end",
};

export const MEDIA_KINDS = ["image", "video", "document", "audio"];

export const BRANCH_TYPES = new Set([
  STAGE_TYPES.BUTTON,
  STAGE_TYPES.MENU,
  STAGE_TYPES.DROPDOWN,
  STAGE_TYPES.RADIO_POPUP,
  STAGE_TYPES.API,
  STAGE_TYPES.CONDITION,
]);

export const SILENT_TYPES = new Set([
  STAGE_TYPES.API,
  STAGE_TYPES.CONDITION,
  STAGE_TYPES.SET_VARIABLE,
  STAGE_TYPES.DELAY,
  STAGE_TYPES.FLOW_LINK,
]);

export function isMessageStage(type) {
  const t = String(type || "").toLowerCase();
  return t === "message" || t === "text" || t === "template";
}

export function isTerminalStage(type) {
  const t = normalizeStageType(type);
  return t === STAGE_TYPES.END || t === STAGE_TYPES.FLOW_LINK;
}

export function isBranchStage(type) {
  return BRANCH_TYPES.has(normalizeStageType(type));
}

export function isListStage(type) {
  const t = normalizeStageType(type);
  return (
    t === STAGE_TYPES.MENU ||
    t === STAGE_TYPES.DROPDOWN ||
    t === STAGE_TYPES.RADIO_POPUP
  );
}

export function listFieldDefaults(type) {
  const t = normalizeStageType(type);
  if (t === STAGE_TYPES.DROPDOWN) {
    return { listButtonText: "Select", sectionTitle: "Choose", listHeader: "" };
  }
  if (t === STAGE_TYPES.RADIO_POPUP) {
    return {
      listButtonText: "View options",
      sectionTitle: "Select one",
      listHeader: "Choose an option",
    };
  }
  return { listButtonText: "Options", sectionTitle: "Menu", listHeader: "" };
}

export function emptyMediaConfig() {
  return { mediaKind: "image", mediaUrl: "", filename: "" };
}

export function normalizeStageType(raw, extras = {}) {
  const t = String(raw || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (t === "input") return STAGE_TYPES.USER_INPUT;
  if (t === "setvariable") return STAGE_TYPES.SET_VARIABLE;
  if (t === "dropdown_list" || t === "dropdownlist") return STAGE_TYPES.DROPDOWN;
  if (
    t === "radio_popup" ||
    t === "radiopopup" ||
    t === "radio_buttons_popup" ||
    t === "bottom_sheet"
  ) {
    return STAGE_TYPES.RADIO_POPUP;
  }
  if (
    t === "menu" ||
    t === "dropdown" ||
    t === "radio_popup" ||
    t === "media" ||
    t === "user_input" ||
    t === "end" ||
    t === "button" ||
    t === "api" ||
    t === "condition" ||
    t === "set_variable" ||
    t === "delay" ||
    t === "flow_link"
  ) {
    return t;
  }
  if (t === "goto_flow" || t === "go_to_flow" || t === "gotoflow") {
    return STAGE_TYPES.FLOW_LINK;
  }
  if (t === "template") return STAGE_TYPES.TEMPLATE;
  if (t === "text") return STAGE_TYPES.TEXT;
  const kind = String(extras.messageKind || extras.kind || "").toLowerCase();
  if (kind === "template" || (t === "message" && extras.templateName && kind !== "text")) {
    return STAGE_TYPES.TEMPLATE;
  }
  if (t === "message" || !t) return STAGE_TYPES.TEXT;
  return t;
}

export function optionHandleId(key) {
  return `opt-${String(key || "").trim()}`;
}

export function optionKeyFromHandle(handleId) {
  const id = String(handleId || "");
  if (!id.startsWith("opt-")) return "";
  return id.slice(4);
}

export function extractPlaceholders(text = "") {
  const source = String(text || "");
  const curly = [...source.matchAll(/\{\{(\d+)\}\}/g)].map((m) => m[1]);
  const hashNum = [...source.matchAll(/#(\d+)#/g)].map((m) => m[1]);
  const hashVar = /#var#/i.test(source) ? ["1"] : [];
  return [...new Set([...curly, ...hashNum, ...hashVar])].sort(
    (a, b) => Number(a) - Number(b),
  );
}

export function emptyTemplateVariables() {
  return { header: {}, body: {}, buttons: {}, mediaUrl: "" };
}

export function emptyApiConfig() {
  return {
    method: "POST",
    url: "",
    headersJson: "",
    bodyTemplate: "",
    responseMapJson: '{"balance":"data.balance"}',
  };
}

export function emptyConditionConfig() {
  return { field: "", op: "exists", value: "" };
}

export function emptySetVariableConfig() {
  return { key: "", value: "" };
}

export function templateVariableFields(tpl) {
  const components = Array.isArray(tpl?.components) ? tpl.components : [];
  const byType = (type) =>
    components.find((row) => String(row.type || "").toUpperCase() === type);
  const header = byType("HEADER");
  const body = byType("BODY");
  const buttons = byType("BUTTONS");
  const headerFormat = String(header?.format || "TEXT").toUpperCase();
  const buttonKeys = [];
  (buttons?.buttons || []).forEach((btn) => {
    if (btn?.url) buttonKeys.push(...extractPlaceholders(btn.url));
  });
  return {
    headerFormat,
    headerKeys: headerFormat === "TEXT" ? extractPlaceholders(header?.text) : [],
    needsMedia: ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat),
    bodyKeys: extractPlaceholders(body?.text),
    buttonKeys: [...new Set(buttonKeys)].sort((a, b) => Number(a) - Number(b)),
  };
}

function pickVariableMap(prev, keys) {
  const old = prev && typeof prev === "object" ? prev : {};
  const next = {};
  (keys || []).forEach((key) => {
    const id = String(key);
    next[id] = old[id] != null ? String(old[id]) : "";
  });
  return next;
}

export function mergeTemplateVariables(prev, fields) {
  const current = prev && typeof prev === "object" ? prev : emptyTemplateVariables();
  return {
    header: pickVariableMap(current.header, fields?.headerKeys || []),
    body: pickVariableMap(current.body, fields?.bodyKeys || []),
    buttons: pickVariableMap(current.buttons, fields?.buttonKeys || []),
    mediaUrl: fields?.needsMedia ? String(current.mediaUrl || "") : "",
    needsMedia: Boolean(fields?.needsMedia),
    headerFormat: fields?.headerFormat || "",
  };
}

export function applyTemplateVariables(text, values) {
  let out = String(text || "");
  Object.entries(values || {}).forEach(([key, value]) => {
    const filled = String(value || "");
    out = out.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), filled || `{{${key}}}`);
  });
  return out;
}

function templateVariablesFromConfig(config) {
  const raw = config?.templateVariables;
  if (!raw || typeof raw !== "object") return emptyTemplateVariables();
  return {
    header: raw.header && typeof raw.header === "object" ? { ...raw.header } : {},
    body: raw.body && typeof raw.body === "object" ? { ...raw.body } : {},
    buttons: raw.buttons && typeof raw.buttons === "object" ? { ...raw.buttons } : {},
    mediaUrl: String(raw.mediaUrl || ""),
    needsMedia: Boolean(raw.needsMedia),
    headerFormat: String(raw.headerFormat || ""),
  };
}

function readConfig(stage) {
  const raw = stage?.config || stage?.config_json;
  if (raw && typeof raw === "object") return { ...raw };
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? { ...parsed } : {};
    } catch {
      return {};
    }
  }
  return {};
}

function stringifyMap(value, fallback) {
  if (value == null || value === "") return fallback;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return fallback;
  }
}

function buttonsToOptions(config) {
  if (Array.isArray(config.options) && config.options.length) {
    return config.options.map((row) => ({
      key: String(row.key || row.id || "").trim(),
      label: String(row.label || row.title || row.key || "").trim(),
      description: String(row.description || "").trim(),
    })).filter((row) => row.key);
  }
  if (Array.isArray(config.buttons)) {
    return config.buttons.map((row, index) => ({
      key: String(row.id || row.key || index + 1).trim(),
      label: String(row.title || row.label || row.id || "").trim(),
      description: String(row.description || "").trim(),
    })).filter((row) => row.key);
  }
  return [];
}

function defaultName(stageType, stageNo, isStart) {
  if (stageType === STAGE_TYPES.BUTTON) return isStart ? "Show Buttons" : `Buttons ${stageNo}`;
  if (stageType === STAGE_TYPES.MENU) return isStart ? "Radio button list" : `Radio list ${stageNo}`;
  if (stageType === STAGE_TYPES.DROPDOWN) return isStart ? "Dropdown list" : `Dropdown ${stageNo}`;
  if (stageType === STAGE_TYPES.RADIO_POPUP) return isStart ? "Radio popup" : `Radio popup ${stageNo}`;
  if (stageType === STAGE_TYPES.MEDIA) return isStart ? "Send media" : `Media ${stageNo}`;
  if (stageType === STAGE_TYPES.USER_INPUT) return `Get User Input`;
  if (stageType === STAGE_TYPES.API) return "API Call";
  if (stageType === STAGE_TYPES.CONDITION) return "Condition";
  if (stageType === STAGE_TYPES.SET_VARIABLE) return "Set Variable";
  if (stageType === STAGE_TYPES.DELAY) return "Delay";
  if (stageType === STAGE_TYPES.FLOW_LINK) return "Go To Flow";
  if (stageType === STAGE_TYPES.END) return "End";
  return isStart ? "Send Message" : `Message ${stageNo}`;
}

export function defaultNodeData(stageType, stageNo, isStart) {
  const type = normalizeStageType(stageType);
  const base = {
    stageNo,
    stageName: defaultName(type, stageNo, isStart),
    stageType: type,
    messageText: "",
    templateName: "",
    messageKind: "text",
    options: [],
    fieldKey: "reply",
    inputKind: "text",
    wrongInputMessage: "",
    isStart,
    isFinal: type === STAGE_TYPES.END,
    api: emptyApiConfig(),
    condition: emptyConditionConfig(),
    setVariable: emptySetVariableConfig(),
    delaySeconds: 3,
    templateVariables: emptyTemplateVariables(),
    listButtonText: "Options",
    sectionTitle: "Menu",
    listHeader: "",
    media: emptyMediaConfig(),
    goToFlowId: "",
  };

  if (type === STAGE_TYPES.BUTTON) {
    return {
      ...base,
      messageText: "Welcome to our services.\nPlease choose an option below.",
      options: [
        { key: "services", label: "View Services" },
        { key: "status", label: "Check Status" },
        { key: "support", label: "Contact Support" },
      ],
      wrongInputMessage: "Invalid option. Please try again.",
    };
  }
  if (type === STAGE_TYPES.MENU) {
    return {
      ...base,
      messageText: "Please choose an option:",
      listButtonText: "Options",
      sectionTitle: "Menu",
      options: [
        { key: "1", label: "Products", description: "" },
        { key: "2", label: "Support", description: "" },
      ],
      wrongInputMessage: "Invalid option. Please try again.",
    };
  }
  if (type === STAGE_TYPES.DROPDOWN) {
    return {
      ...base,
      messageText: "Please select an option:",
      ...listFieldDefaults(type),
      options: [
        { key: "1", label: "Option A", description: "" },
        { key: "2", label: "Option B", description: "" },
      ],
      wrongInputMessage: "Invalid option. Please try again.",
    };
  }
  if (type === STAGE_TYPES.RADIO_POPUP) {
    return {
      ...base,
      messageText: "Tap the button below. Options open from the bottom of WhatsApp.",
      ...listFieldDefaults(type),
      options: [
        { key: "1", label: "Option 1", description: "" },
        { key: "2", label: "Option 2", description: "" },
        { key: "3", label: "Option 3", description: "" },
      ],
      wrongInputMessage: "Invalid option. Please try again.",
    };
  }
  if (type === STAGE_TYPES.MEDIA) {
    return {
      ...base,
      messageText: "",
      media: emptyMediaConfig(),
    };
  }
  if (type === STAGE_TYPES.USER_INPUT) {
    return {
      ...base,
      messageText: "Please reply with your answer.",
      fieldKey: "reply",
    };
  }
  if (type === STAGE_TYPES.API) {
    return {
      ...base,
      options: [
        { key: "success", label: "success" },
        { key: "error", label: "error" },
      ],
      api: emptyApiConfig(),
    };
  }
  if (type === STAGE_TYPES.CONDITION) {
    return {
      ...base,
      options: [
        { key: "true", label: "true" },
        { key: "false", label: "false" },
      ],
      condition: emptyConditionConfig(),
    };
  }
  if (type === STAGE_TYPES.SET_VARIABLE) {
    return { ...base, setVariable: emptySetVariableConfig() };
  }
  if (type === STAGE_TYPES.DELAY) {
    return { ...base, delaySeconds: 3 };
  }
  if (type === STAGE_TYPES.FLOW_LINK) {
    return {
      ...base,
      stageName: isStart ? "Go To Flow" : `Go To Flow ${stageNo}`,
      isStart: false,
      isFinal: false,
      goToFlowId: "",
    };
  }
  if (type === STAGE_TYPES.END) {
    return {
      ...base,
      messageText: "Thank you. Goodbye.",
      isStart: false,
      isFinal: true,
    };
  }
  const kind = type === STAGE_TYPES.TEMPLATE ? STAGE_TYPES.TEMPLATE : STAGE_TYPES.TEXT;
  return {
    ...base,
    stageType: kind,
    messageKind: kind,
    messageText: kind === STAGE_TYPES.TEXT ? "Welcome to Gneiss!\nHow can we help you today?" : "",
  };
}

export function graphToRf({ stages = [], transitions = [] } = {}) {
  const nodes = (stages || []).map((stage, index) => {
    const stageNo = Number(stage.stageNo ?? stage.stage_no ?? index + 1);
    const posX = stage.posX ?? stage.pos_x;
    const posY = stage.posY ?? stage.pos_y;
    const rawType = String(stage.stageType || stage.stage_type || "text").toLowerCase();
    const config = readConfig(stage);
    const templateName = stage.templateName || stage.template_name || "";
    const messageKindHint =
      config.messageKind === "template" || rawType === "template"
        ? "template"
        : config.messageKind === "text" || rawType === "text"
          ? "text"
          : "";
    const stageType = normalizeStageType(rawType, {
      messageKind: messageKindHint,
      templateName,
    });
    const messageKind = isMessageStage(stageType)
      ? stageType === STAGE_TYPES.TEMPLATE
        ? "template"
        : "text"
      : messageKindHint || "text";
    const defaults = defaultNodeData(stageType, stageNo, false);
    let options = defaults.options;
    if (
      stageType === STAGE_TYPES.BUTTON ||
      stageType === STAGE_TYPES.MENU ||
      stageType === STAGE_TYPES.DROPDOWN ||
      stageType === STAGE_TYPES.RADIO_POPUP
    ) {
      const mapped = buttonsToOptions(config);
      if (mapped.length) options = mapped;
    }
    return {
      id: String(stageNo),
      type: "conversation",
      width: 220,
      style: { width: 220 },
      position: {
        x: posX == null ? 80 + (index % 4) * 240 : Number(posX),
        y: posY == null ? 80 + Math.floor(index / 4) * 180 : Number(posY),
      },
      data: {
        ...defaults,
        stageNo,
        stageName: stage.stageName || stage.stage_name || defaultName(stageType, stageNo, false),
        stageType,
        templateName,
        messageText: stage.messageText || stage.message_text || defaults.messageText,
        isStart: Boolean(stage.isStart ?? stage.is_start),
        isFinal: stageType === STAGE_TYPES.END || Boolean(stage.isFinal ?? stage.is_final),
        messageKind,
        options,
        fieldKey: config.fieldKey || defaults.fieldKey,
        inputKind: config.inputKind || defaults.inputKind,
        wrongInputMessage: config.wrongInputMessage || "",
        listButtonText: config.listButtonText || defaults.listButtonText,
        sectionTitle: config.sectionTitle || defaults.sectionTitle,
        listHeader: config.listHeader || defaults.listHeader || "",
        media: {
          ...emptyMediaConfig(),
          mediaKind: String(config.mediaKind || config.media?.mediaKind || "image").toLowerCase(),
          mediaUrl: String(config.mediaUrl || config.media?.mediaUrl || ""),
          filename: String(config.filename || config.media?.filename || ""),
        },
        templateVariables: templateVariablesFromConfig(config),
        api: {
          ...emptyApiConfig(),
          method: String(config.method || config.api?.method || "POST").toUpperCase(),
          url: String(config.url || config.api?.url || ""),
          headersJson: stringifyMap(config.headers || config.api?.headers, ""),
          bodyTemplate: stringifyMap(config.bodyTemplate || config.api?.bodyTemplate, ""),
          responseMapJson: stringifyMap(
            config.responseMap || config.api?.responseMap,
            emptyApiConfig().responseMapJson,
          ),
        },
        condition: {
          field: String(config.condition?.field || config.field || ""),
          op: String(config.condition?.op || config.op || "exists"),
          value: String(config.condition?.value || config.value || ""),
        },
        setVariable: {
          key: String(config.setVariable?.key || config.setKey || ""),
          value: String(config.setVariable?.value || config.setValue || ""),
        },
        delaySeconds: Number(config.delaySeconds || 3) || 3,
        goToFlowId: String(config.goToFlowId || config.go_to_flow_id || ""),
      },
    };
  });

  const edges = (transitions || []).map((transition, index) => {
    const userInput = String(transition.userInput || transition.user_input || "*") || "*";
    const source = String(transition.currentStageNo ?? transition.current_stage_no);
    const target = String(transition.nextStageNo ?? transition.next_stage_no);
    const sourceNode = nodes.find((node) => node.id === source);
    const option = (sourceNode?.data?.options || []).find(
      (row) => String(row.key) === userInput,
    );
    return {
      id: `e-${source}-${userInput}-${target}-${index}`,
      source,
      target,
      type: "default",
      label: userInput,
      data: { userInput },
      markerEnd: { type: "arrowclosed" },
      style: { stroke: "#25d366", strokeWidth: 1.5 },
      zIndex: 1001,
      ...(option
        ? { sourceHandle: optionHandleId(option.key), targetHandle: "t" }
        : {}),
    };
  });

  return { nodes, edges };
}

function parseJsonObject(raw, fallback = {}) {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
  const text = String(raw || "").trim();
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

export function rfToGraph({ nodes = [], edges = [] } = {}) {
  const stages = (nodes || []).map((node) => {
    const stageNo = Number(node.id);
    const config = {};
    const messageKind =
      node.data?.stageType === "template" || node.data?.messageKind === "template"
        ? "template"
        : "text";
    const stageType = normalizeStageType(node.data?.stageType, { messageKind });
    if (isMessageStage(stageType)) {
      config.messageKind = stageType;
    }
    if (
      stageType === STAGE_TYPES.MENU ||
      stageType === STAGE_TYPES.DROPDOWN ||
      stageType === STAGE_TYPES.RADIO_POPUP ||
      stageType === STAGE_TYPES.BUTTON
    ) {
      config.options = (Array.isArray(node.data?.options) ? node.data.options : [])
        .map((row) => ({
          key: String(row.key || row.id || "").trim(),
          label: String(row.label || row.title || row.key || "").trim(),
          description: String(row.description || "").trim(),
        }))
        .filter((row) => row.key);
      if (stageType === STAGE_TYPES.BUTTON) {
        config.buttons = config.options.map((row) => ({
          id: row.key,
          title: row.label || row.key,
        }));
      }
      if (isListStage(stageType)) {
        const fallbacks = listFieldDefaults(stageType);
        config.listButtonText =
          String(node.data?.listButtonText || fallbacks.listButtonText).trim() ||
          fallbacks.listButtonText;
        config.sectionTitle =
          String(node.data?.sectionTitle || fallbacks.sectionTitle).trim() ||
          fallbacks.sectionTitle;
        config.listHeader = String(node.data?.listHeader || fallbacks.listHeader || "").trim();
      }
    }
    if (stageType === STAGE_TYPES.MEDIA) {
      const media = node.data?.media || emptyMediaConfig();
      const kind = String(media.mediaKind || "image").toLowerCase();
      config.mediaKind = MEDIA_KINDS.includes(kind) ? kind : "image";
      config.mediaUrl = String(media.mediaUrl || "").trim();
      config.filename = String(media.filename || "").trim();
    }
    if (stageType === STAGE_TYPES.USER_INPUT) {
      config.fieldKey = String(node.data?.fieldKey || "reply").trim() || "reply";
      config.inputKind = String(node.data?.inputKind || "text").trim() || "text";
    }
    if (stageType === STAGE_TYPES.API) {
      const api = node.data?.api || emptyApiConfig();
      config.method = String(api.method || "POST").toUpperCase();
      config.url = String(api.url || "").trim();
      config.headers = parseJsonObject(api.headersJson, {});
      config.bodyTemplate = parseJsonObject(api.bodyTemplate, {});
      config.responseMap = parseJsonObject(api.responseMapJson, {});
      config.options = [
        { key: "success", label: "success" },
        { key: "error", label: "error" },
      ];
    }
    if (stageType === STAGE_TYPES.CONDITION) {
      const condition = node.data?.condition || emptyConditionConfig();
      config.condition = {
        field: String(condition.field || "").trim(),
        op: String(condition.op || "exists").trim() || "exists",
        value: String(condition.value || ""),
      };
      config.options = [
        { key: "true", label: "true" },
        { key: "false", label: "false" },
      ];
    }
    if (stageType === STAGE_TYPES.SET_VARIABLE) {
      const setVariable = node.data?.setVariable || emptySetVariableConfig();
      config.setVariable = {
        key: String(setVariable.key || "").trim(),
        value: String(setVariable.value || ""),
      };
    }
    if (stageType === STAGE_TYPES.DELAY) {
      config.delaySeconds = Math.max(1, Math.min(15, Number(node.data?.delaySeconds) || 3));
    }
    if (stageType === STAGE_TYPES.FLOW_LINK) {
      config.goToFlowId = String(node.data?.goToFlowId || "").trim();
    }
    const wrongInputMessage = String(node.data?.wrongInputMessage || "").trim();
    if (wrongInputMessage) {
      config.wrongInputMessage = wrongInputMessage;
    }
    if (stageType === STAGE_TYPES.TEMPLATE || node.data?.messageKind === "template") {
      const vars = node.data?.templateVariables;
      if (vars && typeof vars === "object") {
        config.templateVariables = {
          header: vars.header && typeof vars.header === "object" ? vars.header : {},
          body: vars.body && typeof vars.body === "object" ? vars.body : {},
          buttons: vars.buttons && typeof vars.buttons === "object" ? vars.buttons : {},
          mediaUrl: String(vars.mediaUrl || ""),
          needsMedia: Boolean(vars.needsMedia),
          headerFormat: String(vars.headerFormat || ""),
        };
      }
    }
    return {
      stageNo,
      stageName: node.data?.stageName || "",
      stageType,
      templateName: node.data?.templateName || null,
      messageText: node.data?.messageText || "",
      isStart: stageType === STAGE_TYPES.END || stageType === STAGE_TYPES.FLOW_LINK ? false : Boolean(node.data?.isStart),
      isFinal: stageType === STAGE_TYPES.END || Boolean(node.data?.isFinal),
      posX: Math.round(node.position?.x ?? 0),
      posY: Math.round(node.position?.y ?? 0),
      config,
      options: config.options,
      fieldKey: config.fieldKey,
      messageKind: config.messageKind,
      wrongInputMessage: config.wrongInputMessage || "",
      templateVariables: config.templateVariables || emptyTemplateVariables(),
    };
  });

  const transitions = (edges || []).map((edge) => ({
    currentStageNo: Number(edge.source),
    userInput: String(edge.data?.userInput || edge.label || "*").trim() || "*",
    nextStageNo: Number(edge.target),
  }));

  return { stages, transitions };
}

export function nextStageNo(nodes = []) {
  const max = (nodes || []).reduce((acc, node) => {
    const n = Number(node.id);
    return Number.isFinite(n) ? Math.max(acc, n) : acc;
  }, 0);
  return max + 1;
}

function requireOptionConnections(stage, transitions, options, label) {
  if (!options.length) return `${label} "${stage.stageName}" needs at least one option.`;
  for (const option of options) {
    const connected = transitions.some(
      (row) =>
        Number(row.currentStageNo) === Number(stage.stageNo) &&
        String(row.userInput) === String(option.key),
    );
    if (!connected) {
      return `Connect ${label.toLowerCase()} "${option.key}" on "${stage.stageName}".`;
    }
  }
  return "";
}

export function validatePublishGraph({ stages = [], transitions = [] } = {}) {
  if (!stages.length) return "Add at least one stage before publishing.";
  if (!stages.some((s) => s.isStart)) return "Mark one stage as Start.";
  if (
    !stages.some(
      (s) =>
        s.stageType === STAGE_TYPES.END ||
        s.isFinal ||
        s.stageType === STAGE_TYPES.FLOW_LINK,
    )
  ) {
    return "Add an End stage or a Go To Flow node before publishing.";
  }
  for (const stage of stages) {
    if (
      stage.stageType === STAGE_TYPES.MENU ||
      stage.stageType === STAGE_TYPES.DROPDOWN ||
      stage.stageType === STAGE_TYPES.RADIO_POPUP ||
      stage.stageType === STAGE_TYPES.BUTTON
    ) {
      const options = stage.config?.options || stage.options || [];
      const label =
        stage.stageType === STAGE_TYPES.BUTTON
          ? "Button"
          : stage.stageType === STAGE_TYPES.DROPDOWN
            ? "Dropdown list"
            : stage.stageType === STAGE_TYPES.RADIO_POPUP
              ? "Radio popup"
              : "Radio button list";
      const problem = requireOptionConnections(stage, transitions, options, label);
      if (problem) return problem;
      if (stage.stageType === STAGE_TYPES.BUTTON && options.length > 3) {
        return `Button node "${stage.stageName}" can have at most 3 buttons.`;
      }
      if (isListStage(stage.stageType) && options.length > 10) {
        return `${label} "${stage.stageName}" can have at most 10 rows.`;
      }
    }
    if (stage.stageType === STAGE_TYPES.MEDIA) {
      if (!String(stage.config?.mediaUrl || "").trim()) {
        return `Media node "${stage.stageName}" needs a media URL.`;
      }
      const connected = transitions.some(
        (row) => Number(row.currentStageNo) === Number(stage.stageNo),
      );
      if (!connected) return `Connect "${stage.stageName}" to the next stage.`;
    }
    if (stage.stageType === STAGE_TYPES.API) {
      if (!String(stage.config?.url || "").trim()) {
        return `API node "${stage.stageName}" needs a URL.`;
      }
      const problem = requireOptionConnections(
        stage,
        transitions,
        [{ key: "success" }, { key: "error" }],
        "API",
      );
      if (problem) return problem;
    }
    if (stage.stageType === STAGE_TYPES.CONDITION) {
      if (!String(stage.config?.condition?.field || "").trim()) {
        return `Condition "${stage.stageName}" needs a field.`;
      }
      const problem = requireOptionConnections(
        stage,
        transitions,
        [{ key: "true" }, { key: "false" }],
        "Condition",
      );
      if (problem) return problem;
    }
    if (stage.stageType === STAGE_TYPES.SET_VARIABLE) {
      if (!String(stage.config?.setVariable?.key || "").trim()) {
        return `Set Variable "${stage.stageName}" needs a key.`;
      }
    }
    if (
      stage.stageType === STAGE_TYPES.USER_INPUT ||
      stage.stageType === STAGE_TYPES.SET_VARIABLE ||
      stage.stageType === STAGE_TYPES.DELAY
    ) {
      const connected = transitions.some(
        (row) => Number(row.currentStageNo) === Number(stage.stageNo),
      );
      if (!connected) return `Connect "${stage.stageName}" to the next stage.`;
    }
    if (stage.stageType === STAGE_TYPES.FLOW_LINK) {
      if (!String(stage.config?.goToFlowId || stage.goToFlowId || "").trim()) {
        return `Go To Flow node "${stage.stageName}" must select a flow.`;
      }
    }
    if (stage.stageType === STAGE_TYPES.TEMPLATE || stage.config?.messageKind === "template") {
      const vars = stage.config?.templateVariables || stage.templateVariables || {};
      if (vars.needsMedia && !String(vars.mediaUrl || "").trim()) {
        return `Add a header media URL on "${stage.stageName}".`;
      }
      const maps = [vars.header, vars.body, vars.buttons];
      for (const map of maps) {
        if (!map || typeof map !== "object") continue;
        for (const [key, value] of Object.entries(map)) {
          if (!String(value || "").trim()) {
            return `Fill template variable {{${key}}} on "${stage.stageName}".`;
          }
        }
      }
    }
  }
  return "";
}
